import { Form } from 'antd';
import Box from 'components/Box';
import Text from 'components/Text';
import appointmentApis from 'features/appointment/services/apis';
import { first } from 'lodash';
import React, { useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { IStaffItemData } from 'services/shop/types/staff';
import toast from 'utils/toast';
import ModalChooseStaffRef, { useModalChooseStaffRef } from 'widgets/Staff/ModalChooseStaffRef';
import { ModalEditName, NumberCustomers, useModalEditNameRef } from './Customer';
import { CustomerDataItem, CustomerDataItemProps } from './Customer/CustomerDataItem';
import { ChangeServiceModal, ChangeServiceModalRef, QuickBookingV2ServiceItemProps } from './Service';
import { ModalChangeTime, useModalChangeTimeRef } from './TimeDate';
import { IQuickBookingV2CustomerItem, IQuickBookingV2FormValues, IQuickBookingV2ServiceItem } from './type';
import moment from 'moment';
import shopSelectors from 'services/shop/selectors';
type Props = {
  staffs: IStaffItemData[];
  getListStaff: (serviceId: string, servicesProps?: IQuickBookingV2ServiceItem[]) => void;
  onChooseNumCustomer: (value: number) => void;
  isDetail?: boolean;
};

const QuickBookingServiceContent = ({ staffs: staffsProp, getListStaff, onChooseNumCustomer, isDetail }: Props) => {
  const allSetting = shopSelectors.data.allSetting();
  const modalChangeTimeRef = useModalChangeTimeRef();
  const modalEditNameRef = useModalEditNameRef();
  const changeServiceModalRef = useRef<ChangeServiceModalRef>(null);
  const form = Form.useFormInstance();
  const modalChooseStaffRef = useModalChooseStaffRef();
  const [loading, setLoading] = useState(false);
  const [staffs, setStaffs] = useState<IStaffItemData[]>([]);
  const _getListStaff = async (serviceId: string, startTime?: string, endTime?: string) => {
    setLoading(true);
    try {
      const res: IResponseDataBody<IStaffItemData[]> =
        await appointmentApis.getListStaffByTime({ serviceIds: [serviceId], startTime, endTime });
      if (res.data.data) {
        setStaffs(res.data.data);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);
    } finally {
      setLoading(false);
    }
  };

  const onActiveService: QuickBookingV2ServiceItemProps['onActiveService'] = (service, services) => {
    if (isDetail) return;
    if (!service) return;
    form.setFieldValue('activeServiceId', service?.id);
    getListStaff(service?.serviceId, services);
  };

  const onOpenChangeStaff: QuickBookingV2ServiceItemProps['onOpenChangeStaff'] = (service, services) => {
    if (!service) return;
    const date = form.getFieldValue('date')?.format('MM-DD-YYYY') || '';
    const startTime = moment(service.startTime, 'hh:mm A');
    const duration = service.masterDuration || service.duration || 0;
    const endTime = startTime.clone().add(duration, 'minutes');

    _getListStaff(service.serviceId, `${date} ${startTime.format('HH:mm')}`, `${date} ${endTime.format('HH:mm')}`);
    const customerList: IQuickBookingV2FormValues['customerList'] = form.getFieldValue('customerList');
    modalChooseStaffRef.current?.openWithActiveList((staff) => {
      form.setFieldValue('customerList', customerList.map(c => {
        return ({
          ...c,
          services: c.services.map(o => {
            if (o.id === service.id) {
              return ({
                ...o,
                staffId: staff.id,
                staffAvatar: staff.urlImage,
                staffName: staff.nickName,
                anybody: staff.anyBody,
              });
            }
            return o;
          })
        });
      }));
    }, services?.map(o => o.staffId) || []);
  };

  const onEditName = (value?: IQuickBookingV2CustomerItem | undefined, onChange?: (value: IQuickBookingV2CustomerItem) => void) => {
    if (!value || !onChange) return;
    modalEditNameRef.current?.open({ name: value.name }, (result) => {
      onChange({ ...value, name: result.name });
    });
  };
  const onValidateChooseCustomer: CustomerDataItemProps['onValidateChooseCustomer'] = (newCus, customerList = []) => {
    if (customerList.find(o => o.customer?.id === newCus?.id)) {
      toast.error('exist customer');
      return false;
    }
    return true;
  };

  const onItemChangeCustomer: CustomerDataItemProps['onChangeCustomer'] = (itemValue, customerList = []) => (newCus) => {
    if (customerList.find(o => o.customer?.id === newCus?.id)) {
      return toast.error('exist customer');
    }
    const masterCustomerId = form.getFieldValue('masterCustomerId');
    if (masterCustomerId !== itemValue?.customer?.id) return;
    form.setFieldValue('customer', newCus);
    form.setFieldValue('masterCustomerId', newCus?.id);
  };

  return (
    <>
      <Box
        display='flex'
        flexDirection='column'
        style={servicesContainerStyle}
      >
        {isDetail ?
          <Form.Item noStyle name={'numCustomers'}>
            <NumberCustomers onChange={onChooseNumCustomer} />
          </Form.Item> : (<Box>
            {allSetting?.displayBookGroupMultipleCustomers ?
              <Form.Item noStyle name={'numCustomers'}>
                <NumberCustomers onChange={onChooseNumCustomer} />
              </Form.Item>
              : <Form.Item noStyle name={'totalPeople'}>
                <NumberCustomers />
              </Form.Item>}
          </Box>)}
        <Box style={{ flex: 1 }} position='relative' display='flex' flexDirection='column'>
          <Box style={{ inset: 0, overflow: 'auto' }} position='absolute' display='flex' flexDirection='column' gap='2' py='2' px='2'>
            <Form.List name={'customerList'}>
              {(fields) => (
                <>
                  {fields?.length === 1 && <Text variant='H9' color='text_3' className='service-label'>
                    Selected services
                  </Text>}
                  {fields.map((o, index) => {
                    return (
                      <Form.Item key={o.key} name={o.name} noStyle>
                        <CustomerDataItem
                          single={fields?.length === 1}
                          index={index}
                          name={o.name}
                          changeServiceModalRef={changeServiceModalRef}
                          modalChangeTimeRef={modalChangeTimeRef}
                          onActiveService={onActiveService}
                          staffs={staffsProp}
                          getListStaff={getListStaff}
                          onEditName={onEditName}
                          onOpenChangeStaff={onOpenChangeStaff}
                          onChangeCustomer={onItemChangeCustomer}
                          onValidateChooseCustomer={onValidateChooseCustomer}
                        />
                      </Form.Item>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Box>
          <ChangeServiceModal ref={changeServiceModalRef} />
          <Form.Item
            name={'validated'}
            noStyle
            rules={[
              ({ getFieldValue }) => ({
                async validator() {
                  const customerList: IQuickBookingV2FormValues['customerList'] =
                    getFieldValue('customerList');
                  const firstItem = first(customerList);
                  if (!customerList?.length || !firstItem?.services.length) {
                    return Promise.reject(
                      'Please select at least one service'
                    );
                  }

                  const isCustomerListEmptyStaff = customerList.some(o => o.services.some(s => !s.staffId));
                  if (isCustomerListEmptyStaff) {
                    return Promise.reject('Please select at staff');
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <></>
          </Form.Item>
        </Box>
      </Box>
      <ModalChangeTime ref={modalChangeTimeRef} />
      <ModalEditName ref={modalEditNameRef} />
      <ModalChooseStaffRef anybody loading={loading} staffs={staffs} v2 ref={modalChooseStaffRef} noneSearch />
    </>
  );
};

export default QuickBookingServiceContent;

const addServiceButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  border: '2px dashed #2D6772',
  borderRadius: '8px',
  background: '#fff',
  cursor: 'pointer',
  width: '100%',
  justifyContent: 'center',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '16px',
  fontWeight: '600',
  color: '#2D6772',
  transition: 'all 0.2s ease'
};

const servicesContainerStyle: React.CSSProperties = {
  position: 'relative',
  overflow: 'auto',
  paddingRight: 2,
  minWidth: 600,
};
const IconPlus = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='25'
    height='24'
    viewBox='0 0 25 24'
    fill='none'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M12.5 4.5C12.9142 4.5 13.25 4.83579 13.25 5.25V18.75C13.25 19.1642 12.9142 19.5 12.5 19.5C12.0858 19.5 11.75 19.1642 11.75 18.75V5.25C11.75 4.83579 12.0858 4.5 12.5 4.5Z'
      fill='#2D6772'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M5 12C5 11.5858 5.33579 11.25 5.75 11.25H19.25C19.6642 11.25 20 11.5858 20 12C20 12.4142 19.6642 12.75 19.25 12.75H5.75C5.33579 12.75 5 12.4142 5 12Z'
      fill='#2D6772'
    />
    <path
      d='M12.5 4C13.1904 4 13.75 4.55964 13.75 5.25V10.75H19.25C19.9404 10.75 20.5 11.3096 20.5 12C20.5 12.6904 19.9404 13.25 19.25 13.25H13.75V18.75C13.75 19.4404 13.1904 20 12.5 20C11.8096 20 11.25 19.4404 11.25 18.75V13.25H5.75C5.05964 13.25 4.5 12.6904 4.5 12C4.5 11.3096 5.05964 10.75 5.75 10.75H11.25V5.25C11.25 4.55964 11.8096 4 12.5 4ZM12.25 13.25V18.75C12.25 18.8881 12.3619 19 12.5 19C12.6381 19 12.75 18.8881 12.75 18.75V13.25H12.25ZM13.75 12.25H19.25C19.3881 12.25 19.5 12.1381 19.5 12C19.5 11.8619 19.3881 11.75 19.25 11.75H13.75V12.25ZM12.25 12.25H12.75V11.75H12.25V12.25ZM5.75 11.75C5.61193 11.75 5.5 11.8619 5.5 12C5.5 12.1381 5.61193 12.25 5.75 12.25H11.25V11.75H5.75ZM12.5 5C12.3619 5 12.25 5.11193 12.25 5.25V10.75H12.75V5.25C12.75 5.11193 12.6381 5 12.5 5Z'
      fill='#2D6772'
    />
  </svg>
);
